import {
  AddressLink,
  GoldenTicketType,
  IPhaseArgs,
  IPoolArgs,
  ITierLimitArgs,
  PtoContractArgs,
} from 'Modules'
import { parseEventLogs } from 'viem'

import abiPTOFactory from 'abi/abi-pto-factory.json'
import { SettingEnum } from 'constants/setting'
import { useContractViem } from 'hooks/contract/use-contract-viem'
import { useAppSetting } from 'hooks/setting/use-app-setting'
import { useContractManager } from 'hooks/use-contract-manager'
import { clientWallet, publicWallet } from 'libs/viem/config'
import { parserPercentContract, parserUnitContract } from 'utils/contract'
import { logError } from 'utils/logger'

export const useDeployContract = () => {
  const { getSetting } = useAppSetting()

  const ptoFactoryContract = getSetting(SettingEnum.PTO_FACTORY_CONTRACT)

  const contract = useContractViem(ptoFactoryContract, abiPTOFactory)

  const { isContractManager } = useContractManager({ contract })

  const initTierArgs = (tierLimits: ITierLimitArgs[]) => {
    return tierLimits.map((item) => {
      return {
        tierId: item.tierId,
        maxNFTs: item.maxNFTs,
        nftsReserved: item.nftsReserved,
      }
    })
  }

  const initPhaseArgs = (phase: IPhaseArgs[]) => {
    if (!phase) return []
    return phase.map((item) => {
      return {
        tierLimits: initTierArgs(item.tierLimits),
        name: item.name,
        startTime: item.startTime,
        endTime: item.endTime,
        isEndTimeUnlimited: item.isEndTimeUnlimited,
        isPurchasePhase: item.isPurchasePhase,
        isClaimPhase: item.isClaimPhase,
        maxNFTs: item.maxNFTs,
        maxNFTsWhitelisted: item.maxNFTsWhitelisted,
        nftsReserved: item.nftsReserved,
      }
    })
  }

  const initBaseParamsContract = (input?: PtoContractArgs) => {
    if (!input) return null
    if (!input.stakingNFTAddress) return null
    return {
      externalId: input.externalId,
      name: input.name,
      symbol: input.symbol,
      initBaseURI: input.initBaseURI,
      usdcToken: input.usdcToken,
      stakingTierManager: input.stakingTierManager,
      ownerAddress: input.ownerAddress,
      royaltyFee: parserPercentContract(input.royaltyFee),
      whitelistSigner: input.whitelistSigner,
      stakingNFTAddress: input.stakingNFTAddress,
    }
  }

  const initParamsPool = (pool?: IPoolArgs) => {
    if (!pool) return null
    return {
      pricePerNFT: parserUnitContract(pool.pricePerNFT),
      maxNFTSupply: pool.maxNFTSupply,
      buyingFeeBP: parserPercentContract(pool.buyingFeeBP),
      tradeUnlockTime: pool.tradeUnlockTime,
    }
  }

  const initParamsGoldenTicket = (goldenTickets?: GoldenTicketType[], enabled: boolean = true) => {
    if (!enabled) return []
    if (!goldenTickets) return []
    return goldenTickets.map((item, index) => {
      return {
        name: item.name,
        count: item.quantity,
        rarityId: index + 1,
      }
    })
  }

  const deployContract = async (address: AddressLink, input: PtoContractArgs) => {
    try {
      if (!ptoFactoryContract) return null

      if (!address || !input) return null

      const paramsContract = initBaseParamsContract(input)

      const paramsPool = initParamsPool(input?.pool)

      const paramsPhases = initPhaseArgs(input?.phases)

      const paramsGoldenTicket = initParamsGoldenTicket(input?.goldenTickets)

      if (!paramsContract || !paramsPool || !paramsPhases) return null

      const { request } = await contract.simulate.deployCollection(
        [paramsContract, paramsPool, paramsPhases, paramsGoldenTicket],
        {
          account: address,
        }
      )
      const txHash = await clientWallet?.writeContract(request)

      const receipt = await publicWallet.waitForTransactionReceipt({
        hash: txHash,
        confirmations: 1,
      })
      const logs: any = parseEventLogs({
        abi: abiPTOFactory,
        logs: receipt.logs,
        eventName: 'CollectionDeployed',
      })

      if (logs.length > 0) {
        const collectionAddress = logs[0].args.collectionAddress
        return collectionAddress
      }
      return null
    } catch (error) {
      logError('error deploy contract-----------------', error)
      return null
    }
  }

  return {
    isContractManager,
    deployContract,
  }
}
